import { AllHTMLAttributes, ForwardedRef, forwardRef, useState } from 'react';
import { Box, FormHelperText, styled } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import { Control, Controller, ControllerProps } from 'react-hook-form';
import { ErrorFormField } from '../types';

export const StyleDatePicker = styled(DatePicker)({
  '& input:focus': {
    'box-shadow': 'none',
  },
});

type DatePickerProps = AllHTMLAttributes<HTMLDivElement> &
  ErrorFormField & {
    defaultValue?: Dayjs;
    [key: string]: any;
  };

const CustomDatePicker = forwardRef(
  (props: DatePickerProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      className,
      defaultValue,
      onChange,
      error,
      errMessage,
      ...extraProps
    } = props as any;
    const [selectTime, setSelectTime] = useState(
      defaultValue || dayjs(new Date())
    );

    /**
     * methods
     */
    const timeChangeAction = (value) => {
      setSelectTime(value);
      if (onChange) onChange(value);
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <Box className={clsx('flex w-full flex-col', className)}>
          <StyleDatePicker
            ref={ref}
            {...extraProps}
            value={selectTime}
            onChange={timeChangeAction}
          />
          {error && (
            <FormHelperText className="mt-1" error={error}>
              {errMessage}
            </FormHelperText>
          )}
        </Box>
      </LocalizationProvider>
    );
  }
);

type ControlledProps = Omit<ControllerProps, 'control' | 'render'> &
  DatePickerProps & {
    control: Control<any>;
  };

export const DatePickerControlled = forwardRef(
  (
    { control, name, onDateChange, ...props }: ControlledProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const timeChange = (value) => {
            if (onDateChange) onDateChange(value);
            if (onChange) onChange(value);
          };
          return (
            <CustomDatePicker
              {...props}
              ref={ref}
              defaultValue={value}
              onChange={timeChange}
              error={!!error}
              errMessage={error?.message}
            />
          );
        }}
      />
    );
  }
);

export default CustomDatePicker;
